import { ColorPicker } from '@/components/ColorPicker';
import { isValidHexColor } from '@/components/ColorPicker/utils/isValidHexColor';
import { removeHexColorAlphaSuffix } from '@/components/ColorPicker/utils/removeHexColorAlphaSuffix';
import Switch from '@/components/Switch/Switch';
import { ControlRow } from '@/pages/AdvancedEditor/components/panels/common/ControlRow';
import { predefinedColors } from '@/pages/AdvancedEditor/components/panels/common/predefinedColors';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import theme from '@/theme';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { ElementType } from 'polotno/model/group-model';
import { useEffect, useState } from 'react';
import { DEFAULT_SHAPES_FILL_COLOR } from '../ShapeDetails';

const FillColorContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ColorPickerContainer = styled.div`
    display: flex;
    width: min-content;
    gap: 8px;
    align-items: center;
    cursor: pointer;
    border-radius: 8px;
    padding: 6px 8px;

    border: 1px solid ${theme.colors.black[200]};
`;

const InvisibleInput = styled.input`
    border: none;
    outline: none;
    width: auto;
    min-width: 0;
    flex: 0 1 auto;

    color: ${theme.colors.secondary.main};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
`;

const initEnableState = (color: string) => color !== 'transparent';
const initColorState = (color: string) => (color === 'transparent' ? DEFAULT_SHAPES_FILL_COLOR : color);

export const FillColorControl = observer(() => {
    const { store } = useAdvancedEditorContext();

    const elements = store.selectedShapes;
    const element = elements[0]?.toJSON() as ElementType & {
        fill: string;
    };

    const [enabled, setEnabled] = useState<boolean>(initEnableState(element.fill));
    const [color, setColor] = useState<string>(initColorState(element.fill));

    useEffect(() => {
        setEnabled(initEnableState(element.fill));
        setColor(initColorState(element.fill));
    }, [element.id]);

    const handleChange = (color: string) => {
        if (color !== 'transparent') {
            setColor(removeHexColorAlphaSuffix(color));
        }

        elements.forEach(element => element.set({ fill: color }));
    };

    const handleSwitchChange = (_: any, checked: boolean) => {
        if (checked) {
            setEnabled(true);
            handleChange(color);
        } else {
            setEnabled(false);
            handleChange('transparent');
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value.replaceAll('#', '');

        if (isValidHexColor(input)) {
            handleChange('#' + input);
        }
    };

    if (!element) return null;

    return (
        <FillColorContainer>
            <ControlRow>
                Fill
                <Switch checked={enabled} onChange={handleSwitchChange} />
            </ControlRow>
            {enabled && (
                <ColorPickerContainer>
                    <ColorPicker value={color} onChange={handleChange} size="24px" predefinedColors={predefinedColors} />
                    <InvisibleInput
                        size={8}
                        type="text"
                        value={color}
                        onChange={handleInputChange}
                        onClick={e => e.currentTarget.select()}
                    />
                </ColorPickerContainer>
            )}
        </FillColorContainer>
    );
});
